import { createContext, useContext, useRef } from "react";

import { useStore } from "zustand";

import {
  type TypeLogState,
  type TypeLogStore,
  type TypeLogStoreApi,
  createTypeLogStore
} from "./typeLogStore";

export const TypeLogStoreContext = createContext<Maybe<TypeLogStoreApi>>(null);

interface TypeLogStoreProviderProps {
  readonly children: React.ReactNode;
  readonly overrides?: Partial<TypeLogState>;
}

function TypeLogStoreProvider({ children, overrides }: TypeLogStoreProviderProps) {
  const storeRef = useRef<Maybe<TypeLogStoreApi>>(null);
  if (storeRef.current === null) {
    storeRef.current = createTypeLogStore(overrides);
  }
  return (
    <TypeLogStoreContext.Provider value={storeRef.current}>
      {children}
    </TypeLogStoreContext.Provider>
  );
}

/**
 * Hook to select state from the nearest TypeLogStore.
 *
 * Usage (in a Container or Logic Hook — never in a Presenter):
 *   const selectedWell = useTypeLogStore(selectSelectedWell);
 *
 * For compound selectors that return new object literals, wrap the selector
 * with `useShallow` at the call site to avoid re-renders on every store update:
 *
 *   import { useShallow } from "zustand/react/shallow";
 *   const state = useTypeLogStore(useShallow(selectTypeLogState));
 */
function useTypeLogStore<T>(selector: (state: TypeLogStore) => T): T {
  const store = useContext(TypeLogStoreContext);
  if (!store) {
    throw new Error("useTypeLogStore must be used within a <TypeLogStoreProvider>");
  }
  return useStore(store, selector);
}

export { TypeLogStoreProvider, useTypeLogStore };
